import {PenLine, ShieldAlert} from 'lucide-react'
import { useContext, useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import ColonyAvatar from '@/components/ColonyAvatar'
import ColonyAvatarEditor from '@/components/ColonyAvatarEditor'
import ColonyBannerEditor from '@/components/ColonyBannerEditor'
import ColonyFollowButton from '@/components/ColonyFollowButton'
import ColonyOfficialStar from '@/components/ColonyOfficialStar'
import ColonyPostGrid from '@/components/ColonyPostGrid'
import ColonyProfileComposer from '@/components/ColonyProfileComposer'
import Layout from '@/components/Layout'
import SeoHead from '@/components/SeoHead'
import { colonyProfileUrl, parseColonyWallet } from '@/lib/colonyProfile'
import { formatRelativeTime } from '@/lib/format'
import { sameAddress } from '@/lib/launchpad/contracts'
import { isHttpImageUrl } from '@/lib/tokenImage'
import { WalletAddressContext } from '@/lib/walletContext'
import { getFollowStatus, onWalletFavoriteUpdated } from '@/lib/walletFavorites'
import { colonyDisplayName, fetchWalletProfile, onWalletProfileUpdated } from '@/lib/walletProfiles'
import { listWalletPosts, onWalletPostCreated, onWalletPostDeleted, type WalletPost } from '@/lib/walletPosts'

function ColonyProfile() {
  const { wallet: walletParam } = useParams()
  const connectedWallet = useContext(WalletAddressContext)
  const wallet = parseColonyWallet(walletParam)
  const isOwner = sameAddress(connectedWallet, wallet)
  const [image, setImage] = useState('')
  const [banner, setBanner] = useState('')
  const [username, setUsername] = useState('')
  const [posts, setPosts] = useState<WalletPost[]>([])
  const [followersCount, setFollowersCount] = useState(0)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!wallet) {
      setImage('')
      setBanner('')
      setUsername('')
      setPosts([])
      setFollowersCount(0)
      setLoading(false)
      return
    }
    let active = true
    setLoading(true)
    Promise.all([
      fetchWalletProfile(wallet),
      listWalletPosts(wallet),
      getFollowStatus(wallet),
    ]).then(([profile, nextPosts, status]) => {
      if (!active)
        return
      setImage(profile.image)
      setBanner(profile.banner)
      setUsername(profile.username || '')
      setPosts(nextPosts)
      setFollowersCount(status.followersCount)
    }).finally(() => {
      if (active)
        setLoading(false)
    })
    return () => { active = false }
  }, [wallet])

  useEffect(() => {
    return onWalletProfileUpdated((profile) => {
      if (!sameAddress(profile.wallet, wallet))
        return
      setImage(profile.image || '')
      if (typeof profile.username === 'string')
        setUsername(profile.username)
      if (profile.banner)
        setBanner(profile.banner)
    })
  }, [wallet])

  useEffect(() => {
    return onWalletPostCreated((post) => {
      if (!sameAddress(post.wallet, wallet))
        return
      setPosts(prev => [post, ...prev.filter(item => item.id !== post.id)])
    })
  }, [wallet])

  useEffect(() => {
    return onWalletPostDeleted((postId) => {
      setPosts(prev => prev.filter(item => item.id !== postId))
    })
  }, [])

  useEffect(() => {
    if (!wallet)
      return
    return onWalletFavoriteUpdated((event) => {
      if (!sameAddress(event.favoriteWallet, wallet))
        return
      void getFollowStatus(wallet, connectedWallet).then((status) => {
        setFollowersCount(status.followersCount)
      })
    })
  }, [wallet, connectedWallet])

  const bannerSrc = banner && isHttpImageUrl(banner) ? banner : ''

  if (!wallet) {
    return (
      <Layout>
        <SeoHead
          title="Colony profile"
          description="Browse public Penguin Pad Colony profiles."
          url="https://penguinpad.xyz/explore-colony"
        />
        <section className="mx-auto max-w-3xl px-4 py-16 text-center sm:px-6">
          <h1 className="text-3xl font-bold text-white">Colony profile not found</h1>
          <p className="mt-3 text-sm text-zinc-400">That Colony profile could not be found.</p>
          <Link to="/explore-colony" className="mt-6 inline-flex text-sm font-medium text-lime-400">
            Explore the Colony
          </Link>
        </section>
      </Layout>
    )
  }

  return (
    <Layout>
      <SeoHead
        title={`${colonyDisplayName(wallet, username)} — Colony profile`}
        description="Public Penguin Pad Colony profile with photo, posts, and followers on Robinhood Chain."
        image={bannerSrc || image}
        imageAlt={`${colonyDisplayName(wallet, username)} Colony profile`}
        url={colonyProfileUrl(wallet)}
      />

      <section className="mx-auto max-w-4xl px-4 pb-16 pt-8 sm:px-6 lg:px-8">
        <div className={bannerSrc ? 'relative overflow-visible' : undefined}>
          {bannerSrc
            ? (
                <div className="overflow-hidden rounded-3xl border border-white/10 bg-zinc-950">
                  <img
                    src={bannerSrc}
                    alt={`${colonyDisplayName(wallet, username)} banner`}
                    className="aspect-[5/2] w-full object-cover object-center sm:aspect-[4/1]"
                  />
                </div>
              )
            : null}
          {isOwner ? <ColonyBannerEditor currentBanner={bannerSrc} onPublished={setBanner} /> : null}
        </div>

        <div className="mt-6 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div className="flex items-end gap-4">
            {isOwner
              ? <ColonyAvatarEditor />
              : <ColonyAvatar wallet={wallet} image={image} size="lg" anonymous />}
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-lime-400">Colony profile</p>
              <h1 className="flex items-center gap-2 text-2xl font-bold text-white sm:text-3xl">
                <span className="min-w-0 truncate">{colonyDisplayName(wallet, username)}</span>
                <ColonyOfficialStar wallet={wallet} size="lg" />
              </h1>
              <p className="mt-1 text-xs text-zinc-500">
                {followersCount}
                {' '}
                {followersCount === 1 ? 'follower' : 'followers'}
              </p>
            </div>
          </div>
          {isOwner
            ? (
                <Link to="/my-colony" className="rounded-full border border-white/15 px-4 py-2 text-center text-xs font-semibold text-zinc-200 hover:border-lime-400/40">
                  Open My Colony
                </Link>
              )
            : <ColonyFollowButton favoriteWallet={wallet} />}
        </div>

        {isOwner
          ? (
              <div className="mt-8 rounded-3xl border border-lime-400/25 bg-lime-400/[0.06] p-5 backdrop-blur-md sm:p-6">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-lime-400">
                      <PenLine className="h-3.5 w-3.5" aria-hidden="true" />
                      Create a post
                    </p>
                    <h2 className="mt-1 text-lg font-semibold text-white">Share a note or photo</h2>
                    <p className="mt-1 text-xs text-zinc-400">
                      Posts go on this public Colony profile and the Colony Newsfeed. You can attach an official penguinpad.xyz project link.
                    </p>
                  </div>
                  <Link
                    to="/colony-newsfeed"
                    className="inline-flex shrink-0 items-center gap-1 rounded-full border border-white/15 px-3 py-1.5 text-xs font-semibold text-zinc-200 hover:border-lime-400/40 hover:text-white"
                  >
                    Colony Newsfeed
                  </Link>
                </div>
                <div className="mt-4">
                  <ColonyProfileComposer />
                </div>
              </div>
            )
          : null}

        <div className="mt-8 rounded-3xl border border-white/10 bg-white/[0.03] p-5 backdrop-blur-md">
          <h2 className="text-sm font-semibold text-white">Posts</h2>
          <p className="mt-2 flex items-start gap-1.5 text-[11px] leading-relaxed text-amber-200/80">
            <ShieldAlert className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-300" aria-hidden="true" />
            Do not trust outside links in captions. Only official penguinpad.xyz project links appear in lime green and open the project.
          </p>
          {loading
            ? <p className="mt-4 text-sm text-zinc-500">Loading posts…</p>
            : posts.length > 0
              ? <ColonyPostGrid posts={posts} />
              : (
                  <p className="mt-4 text-sm text-zinc-500">
                    {isOwner ? 'No posts yet. Share a note or photo above.' : 'No posts yet from this Colony member.'}
                  </p>
                )}
        </div>
      </section>
    </Layout>
  )
}

export default ColonyProfile
